import LocalizedMessage from '../common/LocalizedMessage';
/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "htmlFor" }] */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, Prompt } from 'react-router-dom';
import DateField from '../common/DateField';
import SchoolCalendarService from '../../services/SchoolCalendarService';
import './schoolCalendar.scss';
import { useTranslation } from 'react-i18next';

const { validateCalendar } = require('../../../LambdaFunctions/shared/schoolCalendar');

function validationMessage(calendar) {
  if (calendar.teachingPeriod.tillDate < calendar.teachingPeriod.fromDate) return 'Koniec zajęć nie może poprzedzać początku.';
  try {
    validateCalendar(calendar);
    return '';
  } catch (error) {
    return 'Sprawdź daty zajęć i przerw. Podaj nazwy przerw, a daty wybierz w obrębie roku szkolnego. Koniec zakresu nie może poprzedzać początku.';
  }
}

function CalendarForm({ schoolYearStart, contextKey, onUnsavedChange }) {
  const { t: tr } = useTranslation();
  const [snapshot, setSnapshot] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const active = useRef(true);
  const nextBreak = useRef(0);
  const settingsPath = `/manage/settings?schoolYearStart=${schoolYearStart}`;
  const minDate = `${schoolYearStart}-09-01`;
  const maxDate = `${schoolYearStart + 1}-08-31`;

  const acceptSnapshot = (data) => {
    setSnapshot(data);
    setCalendar({ schoolYearStart,
      timeZone: data.timeZone || 'Europe/Warsaw',
      teachingPeriod: data.teachingPeriod || { fromDate: '', tillDate: '' },
      breaks: data.breaks || [] });
    setDirty(false);
    setConflict(false);
  };

  useEffect(() => () => { active.current = false; }, []);
  useEffect(() => {
    onUnsavedChange(dirty || saving);
    return () => onUnsavedChange(false);
  }, [dirty, saving, onUnsavedChange]);
  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setSuccess(false);
    if (!contextKey) {
      setError(tr("calendar:theSchoolCouldNotBeIdentifiedPleaseSignIn_135"));
      setLoading(false);
      return () => {};
    }
    SchoolCalendarService.getCalendar(schoolYearStart, { signal: controller.signal }).then((data) => {
      if (current) acceptSnapshot(data);
    }).catch(() => {
      if (current) setError(tr("calendar:couldNotLoadTheSchoolCalendarPleaseTryAgain_136"));
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; controller.abort(); };
  }, [schoolYearStart, contextKey, loadAttempt]);

  useEffect(() => {
    const warn = (event) => {
      if (dirty || saving) {
        event.preventDefault();
        // Browsers require returnValue in addition to preventDefault for this event.
        Object.assign(event, { returnValue: '' });
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saving]);

  const change = (updated) => { setCalendar(updated); setDirty(true); setSuccess(false); setError(''); };
  const changePeriod = (field, value) => change({ ...calendar, teachingPeriod: { ...calendar.teachingPeriod, [field]: value } });
  const changeBreak = (breakId, field, value) => change({ ...calendar,
    breaks: calendar.breaks.map(item => (item.breakId === breakId ? { ...item, [field]: value } : item)) });
  const addBreak = () => {
    nextBreak.current += 1;
    change({ ...calendar, breaks: [...calendar.breaks, { breakId: `break-${Date.now()}-${nextBreak.current}`, name: '', fromDate: '', tillDate: '' }] });
  };
  const save = async (event) => {
    event.preventDefault();
    if (saving || conflict) return;
    const invalid = validationMessage(calendar);
    if (invalid) { setError(invalid); return; }
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const data = await SchoolCalendarService.saveCalendar(schoolYearStart, { expectedRevision: snapshot.revision, calendar });
      if (active.current) { acceptSnapshot(data); setSuccess(true); }
    } catch (failure) {
      if (active.current) {
        if (failure.statusCode === 409 && failure.code !== 'SCHEDULE_UPDATING') {
          setConflict(true);
          setError(tr("calendar:theCalendarHasChangedSinceYouOpenedTheForm_137"));
        } else if (failure.code === 'SCHEDULE_UPDATING') {
          setError(tr("calendar:theSchoolScheduleIsBeingUpdatedWaitAMoment_138"));
        } else if (failure.statusCode === 403) {
          setError(tr("calendar:youDoNotHavePermissionToChangeTheSchool_139"));
        } else {
          setError(tr("calendar:couldNotSaveTheCalendarCheckTheDetailsAnd_140"));
        }
      }
    } finally { if (active.current) setSaving(false); }
  };

  return (
    <section className="school-calendar-panel school-calendar-editor">
      <Prompt when={dirty || saving} message={tr("calendar:youHaveUnsavedCalendarChangesLeaveTheForm_141")} />
      <Link className="btn manage-button manage-teal-button mb-3" to={settingsPath}>{tr("calendar:backToSettings_142")}</Link>
      <h2>{tr("common:schoolCalendar")}</h2>
      <p>{tr("common:schoolYear")} {schoolYearStart}/{schoolYearStart + 1}</p>
      <p className="alert alert-info">{tr("calendar:savedDatesAreAvailableInTheCalendarPreviewThey_143")}</p>
      {error && <p role="alert" className="alert alert-danger"><LocalizedMessage isError value={error} /></p>}
      {success && <p role="status" className="alert alert-success">{tr("calendar:calendarSaved_144")}</p>}
      {loading && <p role="status">{tr("calendar:loadingSchoolCalendar_145")}</p>}
      {!loading && !snapshot && <button type="button" className="btn manage-button manage-teal-button" onClick={() => setLoadAttempt(loadAttempt + 1)}>{tr("manage:tryAgain_183")}</button>}
      {conflict && (
        <button type="button" className="btn manage-button manage-teal-button mb-3" onClick={() => setLoadAttempt(loadAttempt + 1)}>{tr("calendar:loadTheCurrentCalendarAndDiscardMyChanges_146")}</button>
      )}
      {!loading && snapshot && snapshot.status === 'updating' && <p role="status">{tr("calendar:theCalendarIsBeingUpdatedReturnToSettingsAnd_147")}</p>}
      {!loading && snapshot && snapshot.status === 'configured' && !snapshot.integrationPending && <p>{tr("calendar:thisCalendarMustBeUpdatedThroughTheSchoolSchedule_148")}</p>}
      {!loading && calendar && snapshot.status !== 'updating' && (snapshot.status === 'notConfigured' || snapshot.integrationPending) && (
        <form onSubmit={save}>
          <fieldset disabled={saving}>
            <legend>{tr("calendar:teachingPeriod_149")}</legend>
            <div className="row">
              <div className="form-group col-md-6">
                <label htmlFor="calendar-start">{tr("manage:lessonStart_225")}</label>
                <DateField
                  id="calendar-start"
                  aria-label={tr("manage:lessonStart_225")}
                  className="form-control"
                  type="date"
                  required
                  min={minDate}
                  max={maxDate}
                  value={calendar.teachingPeriod.fromDate}
                  onChange={event => changePeriod('fromDate', event.target.value)}
                />
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="calendar-end">{tr("manage:lessonEnd_297")}</label>
                <DateField
                  id="calendar-end"
                  aria-label={tr("manage:lessonEnd_297")}
                  className="form-control"
                  type="date"
                  required
                  min={minDate}
                  max={maxDate}
                  value={calendar.teachingPeriod.tillDate}
                  onChange={event => changePeriod('tillDate', event.target.value)}
                />
              </div>
            </div>
            <h3>{tr("calendar:breaksAndHolidays_150")}</h3>
            <p>{tr("calendar:datesIncludeTheFirstAndLastDayOfThe_151")}</p>
            {calendar.breaks.length === 0 && <p>{tr("calendar:noBreaksOrHolidaysHaveBeenAdded_152")}</p>}
            {calendar.breaks.map((item, index) => (
              <div className="school-calendar-editor-break" key={item.breakId}>
                <div className="form-group">
                  <label htmlFor={`break-name-${item.breakId}`}>{tr("calendar:breakName_153")} {index + 1}</label>
                  <input id={`break-name-${item.breakId}`} className="form-control" required value={item.name} onChange={event => changeBreak(item.breakId, 'name', event.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor={`break-start-${item.breakId}`}>{tr("calendar:breakStart_154")} {index + 1}</label>
                  <DateField
                    id={`break-start-${item.breakId}`}
                    className="form-control"
                    type="date"
                    required
                    min={minDate}
                    max={maxDate}
                    value={item.fromDate}
                    onChange={event => changeBreak(item.breakId, 'fromDate', event.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`break-end-${item.breakId}`}>{tr("calendar:breakEnd_155")} {index + 1}</label>
                  <DateField
                    id={`break-end-${item.breakId}`}
                    className="form-control"
                    type="date"
                    required
                    min={minDate}
                    max={maxDate}
                    value={item.tillDate}
                    onChange={event => changeBreak(item.breakId, 'tillDate', event.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn manage-button manage-teal-button"
                  aria-label={tr('calendar:removeBreak', { number: index + 1 })}
                  onClick={() => change({ ...calendar, breaks: calendar.breaks.filter(entry => entry.breakId !== item.breakId) })}
                >{tr("manage:remove_27")}</button>
              </div>
            ))}
            <button type="button" className="btn manage-button manage-teal-button" onClick={addBreak}>{tr("calendar:addBreak_156")}</button>
            <div className="school-calendar-editor-actions">
              <Link className="btn manage-button manage-teal-button" to={settingsPath}>{tr("common:back")}</Link>
              <button className="btn manage-button manage-red-button" type="submit" disabled={conflict}>{saving ? 'Zapisywanie…' : tr("manage:extraSaveCalendar_133")}</button>
            </div>
          </fieldset>
        </form>
      )}
    </section>
  );
}

const editorProps = { schoolYearStart: PropTypes.number.isRequired, contextKey: PropTypes.string.isRequired, onUnsavedChange: PropTypes.func };
const editorDefaults = { onUnsavedChange: () => {} };
CalendarForm.propTypes = editorProps;
CalendarForm.defaultProps = editorDefaults;

export default function SchoolCalendarEditor({ schoolYearStart, contextKey, onUnsavedChange }) {
  return <CalendarForm key={JSON.stringify([schoolYearStart, contextKey])} schoolYearStart={schoolYearStart} contextKey={contextKey} onUnsavedChange={onUnsavedChange} />;
}
SchoolCalendarEditor.propTypes = editorProps;
SchoolCalendarEditor.defaultProps = editorDefaults;
